import React, { useEffect, useState } from "react";
import { StyledHistoryTechSkill } from "./TechSkillsHistory.styled";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import {
  DESCRIPTION_LABEL,
  DETACH_TECH_SKILL,
  DETACH_TECH_SKILL_MESSAGE,
  EMPTY_COLLABORATOR_TECH_SKILLS_MESSAGE,
  EMPTY_TECH_SKILLS_MESSAGE,
  SEARCH_LABEL,
  TECH_SKILL_LABEL,
  TECH_SKILLS_LABEL,
} from "../../../../../../constants/Dictionary";
import emptyLanguageImage from "../../../../../../images/tecnique.png";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import LoadingThumbnail from "../../../../../../components/LoadingThumbnail/LoadingThumbnail";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../components/core/Link/Link";
import { ReactComponent as AttachIcon } from "../../../../../../icons/attach.svg";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { useDebounce } from "use-debounce";
import { StyledExpandTable } from "../../ProfessionalHistory.styled";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../components/core/Table/Table";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
  CollaboratorsHistoryTechSkill,
} from "../../../../../../types/api/collaborators.type";
import { getCollaboratorHistoryTechSkillsColumns } from "../../../../../../constants/TableColumns";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../../constants/AppConstants";
import { TextBox } from "../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { CollaboratorTechSkillsModal } from "./CollaboratorTechSkillsModal/CollaboratorTechSkillsModal";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import { EditTechSkillCollaboratorModal } from "./EditTechSkillCollaborator/EditTechSkillCollaboratorModal";
import { canEditCollaboratorHistory } from "../../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useHistoryTechSkills,
  useHistoryTechSkillsDelete,
} from "../../../../../../hooks/professionalHistory.hook";

type TechSkillsHistoryProps = {
  addLink: (component: React.ReactNode) => void;
  collaborator: CollaboratorDetails;
  onStatusRefresh: () => void;
  isMyProfile?: boolean;
  setHistoryRefetchFn?: (fn: () => void) => void;
  currentCmSheet?: CollaboratorCmSheet;
};

export const TechSkillsHistory = ({
  addLink,
  collaborator,
  onStatusRefresh,
  isMyProfile,
  currentCmSheet,
}: TechSkillsHistoryProps) => {
  const [open, setOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [selectedRecord, setSelectedRecord] =
    useState<CollaboratorsHistoryTechSkill | null>(null);
  const [openAttachModal, setOpenAttachModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;

  const {
    data: historyTechSkills,
    isLoading: loadingHistoryTechSkill,
    isFetching: fetchingHistoryTechSkill,
  } = useHistoryTechSkills({ profileId, pageNumber, searchDebouncedValue });

  const { mutateAsync: deleteHistoryTechSkill, isPending } =
    useHistoryTechSkillsDelete(profileId);

  const hasData =
    historyTechSkills !== undefined
      ? historyTechSkills.pagination.total > 0 || !!searchDebouncedValue
      : null;

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
  );

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleSaveSuccess = async () => {
    setOpen(false);
    setSelectedRecord(null);
    onStatusRefresh();
  };

  useEffect(() => {
    if (hasData && canEditHistory) {
      addLink(
        <Link
          onClick={handleOpenModal}
          icon={<AttachIcon />}
          size={LinkSizeEnum.SMALL}
          disabled={fetchingHistoryTechSkill}
        >
          {TECH_SKILL_LABEL}
        </Link>,
      );
    }
  }, [hasData, canEditHistory, fetchingHistoryTechSkill]);

  const rowKey = (record: CollaboratorsHistoryTechSkill) => {
    return record.id.toString();
  };

  const hideAttachModal = () => {
    setOpenAttachModal(false);
    setSelectedRecord(null);
  };

  const onDetachTechSkill = (record: CollaboratorsHistoryTechSkill) => {
    setSelectedRecord(record);
    setOpenAttachModal(true);
  };

  const handleDetachTechSkill = async () => {
    await deleteHistoryTechSkill(selectedRecord!.id);
    hideAttachModal();
    onStatusRefresh();
  };

  const onEdit = (record: CollaboratorsHistoryTechSkill) => {
    setSelectedRecord(record);
    setShowEditModal(true);
  };

  const expandedRow = (record: CollaboratorsHistoryTechSkill) => {
    return <TextBox label={DESCRIPTION_LABEL} text={record.description} />;
  };

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  return (
    <>
      <StyledHistoryTechSkill>
        {loadingHistoryTechSkill &&
        hasData === null &&
        !searchDebouncedValue ? (
          <Loader loading>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : (
          <>
            {!hasData && !fetchingHistoryTechSkill ? (
              <div className="empty-state">
                <EmptyState
                  label={EMPTY_COLLABORATOR_TECH_SKILLS_MESSAGE}
                  imageSrc={emptyLanguageImage}
                >
                  {canEditHistory && (
                    <Button
                      label={TECH_SKILL_LABEL}
                      variant={ButtonVariants.SECONDARY}
                      size={ButtonSizes.REGULAR}
                      icon={<AttachIcon />}
                      onClick={handleOpenModal}
                    />
                  )}
                </EmptyState>
              </div>
            ) : (
              <>
                <DoubleStructure
                  verticalAlignment={AlignmentEnum.CENTER}
                  variant="primary"
                >
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {!fetchingHistoryTechSkill &&
                      `${historyTechSkills?.pagination.total} ${historyTechSkills?.pagination.total === 1 ? TECH_SKILL_LABEL : TECH_SKILLS_LABEL}`}
                  </BodyTyp>
                  <div className="filters">
                    <SearchInput
                      onChange={setSearchValue}
                      label={`${SEARCH_LABEL}...`}
                      name={"search"}
                      value={searchValue}
                      disabled={fetchingHistoryTechSkill || isPending}
                    />
                  </div>
                </DoubleStructure>
                <StyledExpandTable>
                  <Table<CollaboratorsHistoryTechSkill>
                    theme={TableThemeEnum.DARK}
                    loading={fetchingHistoryTechSkill}
                    rowKey={rowKey}
                    headers={getCollaboratorHistoryTechSkillsColumns(
                      orgSettings,
                      true,
                    )}
                    scrollY={"calc(100dvh - 502px)"}
                    data={historyTechSkills?.data ?? []}
                    emptyText={EMPTY_TECH_SKILLS_MESSAGE}
                    onEdit={canEditHistory ? onEdit : undefined}
                    onDetach={canEditHistory ? onDetachTechSkill : undefined}
                    pagination={{
                      current: pageNumber + 1,
                      pageSize: COLLABORATORS_PAGE_SIZE,
                      total: historyTechSkills?.pagination.total ?? 0,
                      onChange: (pageNumber) => setPageNumber(pageNumber - 1),
                    }}
                    expandable={{
                      expandedRowRender: expandedRow,
                    }}
                  />
                </StyledExpandTable>
              </>
            )}
          </>
        )}
      </StyledHistoryTechSkill>
      <CollaboratorTechSkillsModal
        isOpen={open}
        onCancel={() => setOpen(false)}
        onConfirm={handleSaveSuccess}
        {...(selectedRecord && {
          record: selectedRecord,
        })}
        collaborator={collaborator}
      />
      <PopupConfirm
        isOpen={openAttachModal}
        onCancel={hideAttachModal}
        onConfirm={selectedRecord ? () => handleDetachTechSkill() : () => {}}
        label={DETACH_TECH_SKILL}
        isLoading={isPending}
        popupText={
          selectedRecord
            ? `${DETACH_TECH_SKILL_MESSAGE} ${selectedRecord.competence}?`
            : "error"
        }
      />
      {selectedRecord && (
        <EditTechSkillCollaboratorModal
          record={selectedRecord}
          isOpen={showEditModal}
          closeModal={() => {
            setShowEditModal(false);
            setSelectedRecord(null);
          }}
          updateList={async () => {
            onStatusRefresh();
          }}
          collaborator={collaborator}
        />
      )}
    </>
  );
};
